import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { Sparkles } from "lucide-react";
import { FC, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import {
  getGetDoclistQueryKey,
  useCreateChat,
  useGetDoclist,
} from "~/api/chat/chat";
import { Folder, FolderChatStatus } from "~/api/model";
import { getGetProjectQueryKey, useGetProject } from "~/api/projects/projects";
import { FancyButton } from "~/components/fancy-button";
import { Loader } from "~/components/Loader";
import { Button } from "~/components/ui/button";
import { LargeDocumentAlert } from "~/features/case/components/LargeDocumentAlert";
import { ROUTES, useProjectParams } from "~/helpers";
import { useWebsocket } from "~/hooks/useWebsocket";
import { useAuthStore } from "~/stores/authStore";
import { trackEvent } from "~/utils/analytics";
import {
  estimateRemainingSeconds,
  formatRemainingDuration,
} from "~/utils/processingEstimate";
import ConditionalTooltip from "./ConditionalTooltip";

const processingStartTimes = new Map<string, number>();
// Page count above which a case is considered "large" and the first Launch Sky
// action surfaces the manual-verification warning modal (SKY-3066).
const LARGE_CASE_PAGE_THRESHOLD = 2000;
// Persists which cases have already shown the large-case warning modal, so the
// reminder only appears on the first "Launch Sky" action per case.
const LARGE_CASE_WARNING_SEEN_PREFIX = "sky:largeCaseWarningSeen:";

const hasSeenLargeCaseWarning = (caseId: string): boolean => {
  try {
    return (
      localStorage.getItem(`${LARGE_CASE_WARNING_SEEN_PREFIX}${caseId}`) ===
      "true"
    );
  } catch {
    return false;
  }
};

const markLargeCaseWarningSeen = (caseId: string): void => {
  try {
    localStorage.setItem(`${LARGE_CASE_WARNING_SEEN_PREFIX}${caseId}`, "true");
  } catch {
    // localStorage may be unavailable (private mode / quota); the worst case is
    // that the warning is shown again on the next launch, which is acceptable.
  }
};
// Tracks caseIds for which the "Launch Sky" button enabled state has already
// been reported to Amplitude in the current page session, so re-mounts (mobile
// vs desktop instances of NewChatButton) don't double-count.
const readyTrackedCaseIds = new Set<string>();

type NewChatButtonProps = {
  documentIds: string[];
  disabled?: boolean;
  totalPages?: number;
  completedPages?: number;
  mobileLabel?: string;
  useMobileStyle?: boolean;
};

export const NewChatButton: FC<NewChatButtonProps> = ({
  documentIds,
  disabled = false,
  totalPages = 0,
  completedPages = 0,
  mobileLabel,
  useMobileStyle = false,
}) => {
  const { organizationId, caseId } = useProjectParams();
  const { data: currentCase, refetch: refetchProject } = useGetProject(
    organizationId,
    caseId
  );
  const { mutateAsync: createChat } = useCreateChat();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isChatPending, setIsChatPending] = useState(false);
  const intl = useIntl();
  const [remainingTime, setRemainingTime] = useState<number | null>(null);
  const [largeCaseAlertOpened, setLargeCaseAlertOpened] =
    useState<boolean>(false);
  const isAuthExpired = useAuthStore((state) => state.isAuthExpired);

  const { addListener, removeListener } = useWebsocket<{ folder: Folder }>({
    projectId: caseId,
    subscribeTo: "folder-update-log",
  });

  const isCaseReady = currentCase?.chatStatus === FolderChatStatus.COMPLETE;
  const { isError } = useGetDoclist(organizationId, caseId, undefined, {
    query: {
      enabled: isCaseReady && documentIds.length > 0,
    },
  });

  const isLargeCase = totalPages >= LARGE_CASE_PAGE_THRESHOLD;

  const openChat = async () => {
    setIsChatPending(true);
    const chat = await createChat({
      organizationId,
      projectId: caseId,
      data: {
        documentIds,
        title: currentCase?.name ?? "Untitled Chat",
      },
    });

    setIsChatPending(false);

    navigate(
      ROUTES.CHAT_DOCUMENT({
        organizationId,
        caseId,
        chatId: chat.id,
      })
    );
  };

  const handleClick = async () => {
    if (isLargeCase && !hasSeenLargeCaseWarning(caseId)) {
      setLargeCaseAlertOpened(true);
      return;
    }
    await openChat();
  };

  const handleLargeCaseAlertOpenChange = (open: boolean) => {
    setLargeCaseAlertOpened(open);
    if (!open) {
      markLargeCaseWarningSeen(caseId);
    }
  };

  const handleLaunchSkyFromAlert = async () => {
    markLargeCaseWarningSeen(caseId);
    setLargeCaseAlertOpened(false);
    await openChat();
  };

  const tooltipMessage = useMemo(() => {
    if (isAuthExpired) {
      return (
        <FormattedMessage
          id="documentsList.sessionExpired"
          defaultMessage="Session expired. Please sign in again."
        />
      );
    }

    if (isError) {
      return (
        <FormattedMessage
          id="documentsList.docListError"
          defaultMessage="Sky is unavailable. Please contact support team."
        />
      );
    }

    return undefined;
  }, [isAuthExpired, isError]);

  useEffect(() => {
    const handler = (updates: { folder: Folder }[]) => {
      const folder = { ...updates[0].folder };
      const key = getGetProjectQueryKey(organizationId, caseId);
      let shouldInvalidate = false;
      let shouldInvalidateDoclist = false;

      queryClient.setQueryData<Folder | undefined>(key, (old) => {
        if (!old) {
          shouldInvalidate = true;
          return old;
        }

        if (
          folder.chatStatus === FolderChatStatus.COMPLETE ||
          folder.chatStatus === FolderChatStatus.ERROR
        ) {
          shouldInvalidate = true;
          if (folder.chatStatus === FolderChatStatus.COMPLETE) {
            shouldInvalidateDoclist = true;
          }
        }

        return folder;
      });

      if (shouldInvalidate) {
        queryClient.invalidateQueries({ queryKey: key });
      }

      if (shouldInvalidateDoclist) {
        queryClient.invalidateQueries({
          queryKey: getGetDoclistQueryKey(organizationId, caseId),
        });
      }
    };
    addListener(handler);
    return () => removeListener(handler);
  }, [addListener, removeListener, organizationId, caseId, queryClient]);

  useEffect(() => {
    const isProcessing =
      currentCase?.chatStatus === FolderChatStatus.PROCESSING ||
      currentCase?.chatStatus === FolderChatStatus.COPYING;

    if (!isProcessing || isAuthExpired) {
      return;
    }

    const pollInterval = setInterval(() => {
      refetchProject();
    }, 15000);

    return () => clearInterval(pollInterval);
  }, [currentCase?.chatStatus, refetchProject, isAuthExpired]);

  const isButtonDisabled =
    disabled ||
    isAuthExpired ||
    isError ||
    currentCase?.chatStatus !== FolderChatStatus.COMPLETE ||
    isChatPending;

  useEffect(() => {
    if (isButtonDisabled) return;
    if (readyTrackedCaseIds.has(caseId)) return;

    readyTrackedCaseIds.add(caseId);

    const processingStartedAt = processingStartTimes.get(caseId);
    trackEvent({
      eventName: "app/case/launch_sky.button/enabled",
      properties: {
        organizationId,
        caseId,
        documentCount: documentIds.length,
        totalPages,
        completedPages,
        ...(processingStartedAt !== undefined && {
          timeToReadyMs: Date.now() - processingStartedAt,
        }),
      },
    });
  }, [
    isButtonDisabled,
    caseId,
    organizationId,
    documentIds.length,
    totalPages,
    completedPages,
  ]);

  const buttonClassName = useMemo(() => {
    const isErrorState =
      isAuthExpired ||
      isError ||
      currentCase?.chatStatus === FolderChatStatus.ERROR;

    return clsx("gap-2 text-white", {
      "bg-red-600 hover:bg-red-700": isErrorState,
      "bg-blue-600 hover:bg-blue-700 active:scale-95 transition-all duration-300":
        !isErrorState,
    });
  }, [isAuthExpired, isError, currentCase?.chatStatus]);

  const showProcessing =
    (disabled ||
      currentCase?.chatStatus === FolderChatStatus.PROCESSING ||
      currentCase?.chatStatus === FolderChatStatus.COPYING) &&
    currentCase?.chatStatus !== FolderChatStatus.ERROR;

  useEffect(() => {
    if (showProcessing && totalPages > 0) {
      if (!processingStartTimes.has(caseId)) {
        processingStartTimes.set(caseId, Date.now());
      }
    } else {
      processingStartTimes.delete(caseId);
      setRemainingTime(null);
    }
  }, [showProcessing, totalPages, caseId]);

  useEffect(() => {
    const startTime = processingStartTimes.get(caseId);
    if (!showProcessing || startTime === undefined) {
      return;
    }

    const updateEstimate = () => {
      const currentStartTime = processingStartTimes.get(caseId);
      if (currentStartTime === undefined) return;
      const elapsedSeconds = Math.floor((Date.now() - currentStartTime) / 1000);
      const estimate = estimateRemainingSeconds({
        completedPages,
        totalPages,
        elapsedSeconds,
      });
      setRemainingTime(estimate);
    };

    updateEstimate();
    const interval = setInterval(updateEstimate, 5000);

    return () => clearInterval(interval);
  }, [showProcessing, completedPages, totalPages, caseId]);

  if (documentIds.length === 0) {
    return null;
  }

  return (
    <>
      <LargeDocumentAlert
        open={largeCaseAlertOpened}
        onOpenChange={handleLargeCaseAlertOpenChange}
        launchSky={handleLaunchSkyFromAlert}
      />
      <ConditionalTooltip message={tooltipMessage}>
        <div className="group">
          {currentCase?.chatStatus === FolderChatStatus.COMPLETE &&
            (useMobileStyle ? (
              <Button
                data-tour="launch-sky"
                disabled={isButtonDisabled}
                onClick={handleClick}
                variant="ghost"
                className="text-brand hover:text-brand/90"
              >
                <Sparkles className="h-4 w-4 mr-1" />
                {mobileLabel ??
                  intl.formatMessage({
                    id: "documentsList.launchSky",
                    defaultMessage: "Launch Sky",
                  })}
              </Button>
            ) : (
              <div
                data-tour="launch-sky"
                onClick={isButtonDisabled ? undefined : handleClick}
                style={{
                  cursor: isButtonDisabled ? "not-allowed" : "pointer",
                  opacity: isButtonDisabled ? 0.6 : 1,
                }}
              >
                <FancyButton
                  title={
                    mobileLabel ??
                    intl.formatMessage({
                      id: "documentsList.launchSky",
                      defaultMessage: "Launch Sky",
                    })
                  }
                />
              </div>
            ))}
          {currentCase?.chatStatus !== FolderChatStatus.COMPLETE && (
            <Button
              disabled={isButtonDisabled}
              onClick={handleClick}
              className={buttonClassName}
            >
              {isAuthExpired ? (
                <FormattedMessage
                  id="documentsList.sessionExpired"
                  defaultMessage="Session expired. Please sign in again."
                />
              ) : showProcessing ? (
                <>
                  <Loader
                    isShowPng={true}
                    isWhite={true}
                    className="h-4 w-4 animate-spin"
                  />
                  {currentCase?.chatStatus === FolderChatStatus.COPYING ? (
                    <FormattedMessage
                      id="documentsList.almostDone"
                      defaultMessage="Almost done..."
                    />
                  ) : totalPages > 0 && remainingTime !== null ? (
                    remainingTime > 0 ? (
                      <FormattedMessage
                        id="documentsList.etaRemaining"
                        defaultMessage="{estimate} remaining"
                        values={{
                          estimate: formatRemainingDuration(
                            remainingTime,
                            intl.locale
                          ),
                        }}
                      />
                    ) : (
                      <FormattedMessage
                        id="documentsList.soon"
                        defaultMessage="Soon..."
                      />
                    )
                  ) : (
                    <FormattedMessage
                      id="documentsList.preparingChat"
                      defaultMessage="Processing..."
                    />
                  )}
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 transition-transform duration-300 group-hover:scale-125" />
                  <FormattedMessage
                    id="documentsList.launchSky"
                    defaultMessage="Launch Sky"
                  />
                </>
              )}
            </Button>
          )}
        </div>
      </ConditionalTooltip>
    </>
  );
};
